'use client';

import React, { useState, useEffect } from 'react';
import ServiceHero from './ServiceHero';
import ServiceCard from './ServiceCard';
import ServiceComparison from './ServiceComparison';
import TestimonialCard from './TestimonialCard';
import IntegrationPartners from './IntegrationPartners';
import CTASection from './CTASection';

interface Feature {
  title: string;
  description: string;
}

interface Service {
  id: string;
  icon: string;
  title: string;
  description: string;
  features: Feature[];
  image: string;
  imageAlt: string;
  pricing: string;
}

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  company: string;
  image: string;
  imageAlt: string;
  rating: number;
}

interface ComparisonFeature {
  feature: string;
  basic: boolean;
  professional: boolean;
  enterprise: boolean;
}

interface Partner {
  name: string;
  logo: string;
  logoAlt: string;
}

const ServicesInteractive: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [expandedServices, setExpandedServices] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const heroData = {
    title: "Comprehensive Digital Solutions for Your Business Growth",
    description: "From stunning web development to strategic SEO optimization, we deliver integrated solutions that drive measurable results and lasting competitive advantages.",
    stats: [
    { value: "500+", label: "Projects Delivered" },
    { value: "98%", label: "Client Satisfaction" },
    { value: "250%", label: "Avg. ROI Increase" }]

  };

  const services: Service[] = [
  {
    id: "web-development",
    icon: "CodeBracketIcon",
    title: "Custom Web Development",
    description: "Tailored web solutions built with cutting-edge technologies for optimal performance and scalability.",
    features: [
    {
      title: "Responsive Design",
      description: "Mobile-first approach ensuring flawless experience across all devices and screen sizes"
    },
    {
      title: "Performance Optimization",
      description: "Lightning-fast load times with Core Web Vitals optimization and CDN integration"
    },
    {
      title: "Scalable Architecture",
      description: "Future-proof solutions built to grow with your business needs"
    },
    {
      title: "Security Implementation",
      description: "Enterprise-grade security with SSL, data encryption, and regular audits"
    }],

    image: "https://images.unsplash.com/photo-1555066931-bf19f8fd1085",
    imageAlt: "Modern laptop displaying colorful code editor with multiple programming languages on dark background",
    pricing: "$5,000"
  },
  {
    id: "ecommerce",
    icon: "ShoppingCartIcon",
    title: "E-Commerce Solutions",
    description: "Complete online store development with seamless payment integration and inventory management.",
    features: [
    {
      title: "Payment Gateway Integration",
      description: "Secure checkout with multiple payment options including Stripe, PayPal, and more"
    },
    {
      title: "Inventory Management",
      description: "Real-time stock tracking with automated alerts and supplier integration"
    },
    {
      title: "Customer Analytics",
      description: "Comprehensive insights into customer behavior and purchasing patterns"
    },
    {
      title: "Marketing Tools",
      description: "Built-in email campaigns, discount codes, and abandoned cart recovery"
    }],

    image: "https://images.unsplash.com/photo-1544365712-91cd4904cd07",
    imageAlt: "Person holding smartphone showing online shopping cart with product images on screen",
    pricing: "$8,000"
  },
  {
    id: "technical-seo",
    icon: "MagnifyingGlassIcon",
    title: "Technical SEO Optimization",
    description: "Advanced SEO strategies to improve search rankings and drive organic traffic growth.",
    features: [
    {
      title: "Site Audit & Analysis",
      description: "Comprehensive technical audit identifying and fixing critical SEO issues"
    },
    {
      title: "Schema Markup",
      description: "Structured data implementation for enhanced search result visibility"
    },
    {
      title: "Core Web Vitals",
      description: "Performance optimization meeting Google's latest ranking criteria"
    },
    {
      title: "Link Building",
      description: "Strategic backlink acquisition from high-authority domains"
    }],

    image: "https://images.unsplash.com/photo-1674027001838-09e049c47212",
    imageAlt: "Analytics dashboard displaying colorful graphs and charts with SEO metrics on computer screen",
    pricing: "$3,500"
  },
  {
    id: "content-optimization",
    icon: "DocumentTextIcon",
    title: "Content Optimization",
    description: "Data-driven content strategies that engage audiences and convert visitors into customers.",
    features: [
    {
      title: "Keyword Research",
      description: "In-depth analysis identifying high-value keywords with conversion potential"
    },
    {
      title: "Content Strategy",
      description: "Comprehensive content calendar aligned with business goals and user intent"
    },
    {
      title: "On-Page SEO",
      description: "Optimization of meta tags, headings, and content structure for maximum visibility"
    },
    {
      title: "Performance Tracking",
      description: "Regular reporting on content performance and ranking improvements"
    }],

    image: "https://images.unsplash.com/photo-1612772992614-bc2c2a2c3362",
    imageAlt: "Professional woman with glasses writing content strategy notes on whiteboard with colorful markers",
    pricing: "$2,500"
  },
  {
    id: "local-seo",
    icon: "MapPinIcon",
    title: "Local SEO Services",
    description: "Dominate local search results and attract nearby customers with targeted optimization.",
    features: [
    {
      title: "Google Business Profile",
      description: "Complete optimization and management of your Google Business listing"
    },
    {
      title: "Local Citations",
      description: "Consistent NAP (Name, Address, Phone) across all major directories"
    },
    {
      title: "Review Management",
      description: "Proactive reputation management with review generation and response"
    },
    {
      title: "Local Link Building",
      description: "Acquisition of location-specific backlinks from community sources"
    }],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a0b7563b-1765794380085.png",
    imageAlt: "Business team collaborating around table with laptop showing local business map with location pins",
    pricing: "$1,800"
  },
  {
    id: "integrated-solutions",
    icon: "CubeTransparentIcon",
    title: "Integrated Web + SEO Packages",
    description: "Complete digital transformation combining web development excellence with strategic SEO.",
    features: [
    {
      title: "Unified Strategy",
      description: "Seamless integration of design, development, and SEO from project inception"
    },
    {
      title: "Ongoing Optimization",
      description: "Continuous improvements based on performance data and market trends"
    },
    {
      title: "Priority Support",
      description: "Dedicated account manager and 24/7 technical support"
    },
    {
      title: "Monthly Reporting",
      description: "Comprehensive analytics showing traffic growth, rankings, and conversions"
    }],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_171170bd3-1764687877901.png",
    imageAlt: "Diverse team of professionals collaborating at modern office desk with laptops and digital devices",
    pricing: "$12,000"
  }];


  const testimonials: Testimonial[] = [
  {
    quote: "Wilkam Consultancy transformed our online presence completely. Within 6 months, our organic traffic increased by 340% and we're now ranking #1 for our primary keywords. Their integrated approach to web development and SEO is unmatched.",
    author: "Sarah Mitchell",
    role: "Marketing Director",
    company: "TechVision Solutions",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ffde516e-1763293580273.png",
    imageAlt: "Professional woman with brown hair in navy blazer smiling confidently at camera in modern office",
    rating: 5
  },
  {
    quote: "The custom e-commerce platform they built for us exceeded all expectations. Sales have tripled, and the SEO optimization has brought us customers we never knew existed. Best investment we've made in our business.",
    author: "Michael Chen",
    role: "CEO",
    company: "Urban Lifestyle Co",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f464affb-1763299293462.png",
    imageAlt: "Asian businessman in gray suit with short black hair smiling warmly in corporate setting",
    rating: 5
  },
  {
    quote: "Their local SEO services put our restaurant on the map—literally! We went from page 3 to the top 3 local results in just 4 months. Phone reservations have increased by 200% and we're consistently fully booked.",
    author: "Elena Rodriguez",
    role: "Owner",
    company: "Bella Vista Restaurant",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b95d368b-1763293781944.png",
    imageAlt: "Hispanic woman with long dark hair in white chef coat smiling in restaurant kitchen",
    rating: 5
  }];


  const comparisonFeatures: ComparisonFeature[] = [
  { feature: "Custom Website Design", basic: true, professional: true, enterprise: true },
  { feature: "Mobile Responsive", basic: true, professional: true, enterprise: true },
  { feature: "Basic SEO Setup", basic: true, professional: true, enterprise: true },
  { feature: "SSL Certificate", basic: true, professional: true, enterprise: true },
  { feature: "Content Management System", basic: false, professional: true, enterprise: true },
  { feature: "Advanced SEO Strategy", basic: false, professional: true, enterprise: true },
  { feature: "E-Commerce Integration", basic: false, professional: true, enterprise: true },
  { feature: "Monthly Performance Reports", basic: false, professional: true, enterprise: true },
  { feature: "Priority Support (24/7)", basic: false, professional: false, enterprise: true },
  { feature: "Dedicated Account Manager", basic: false, professional: false, enterprise: true },
  { feature: "Custom API Integrations", basic: false, professional: false, enterprise: true },
  { feature: "Advanced Analytics Dashboard", basic: false, professional: false, enterprise: true }];


  const partners: Partner[] = [
  {
    name: "Google Partner",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_192e5c8b2-1764634732588.png",
    logoAlt: "Google Partner certification badge with colorful G logo on white background"
  },
  {
    name: "Shopify Partner",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_171ef228f-1764656420588.png",
    logoAlt: "Shopify Partner logo with green shopping bag icon on white background"
  },
  {
    name: "WordPress Expert",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_15e1d1bba-1768439083219.png",
    logoAlt: "WordPress logo with blue W icon in circular design"
  },
  {
    name: "HubSpot Certified",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1d76c2a3e-1764634734149.png",
    logoAlt: "HubSpot certification badge with orange sprocket logo"
  },
  {
    name: "Semrush Partner",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_155b8e635-1766561206613.png",
    logoAlt: "Semrush partner logo with orange and white branding"
  },
  {
    name: "AWS Certified",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1235aa0fb-1764655536966.png",
    logoAlt: "Amazon Web Services certification badge with orange smile logo"
  }];


  const ctaData = {
    title: "Ready to Transform Your Digital Presence?",
    description: "Let\'s discuss how our integrated web development and SEO services can drive measurable growth for your business. Get a free consultation and custom proposal today.",
    primaryCTA: {
      text: "Get Free Consultation",
      href: "/contact"
    },
    secondaryCTA: {
      text: "Call Us Now",
      href: "/contact"
    }
  };

  const handleToggleService = (serviceId: string) => {
    if (!isHydrated) return;

    setExpandedServices((prev) => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/services/components/ServicesInteractive.tsx:335:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="335" data-component-end-line="347" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <ServiceHero data-component-id="src/app/services/components/ServicesInteractive.tsx:336:8" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="336" data-component-end-line="336" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceHero" data-component-content="%7B%22elementName%22%3A%22ServiceHero%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...heroData} />
        <div data-component-id="src/app/services/components/ServicesInteractive.tsx:337:8" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="337" data-component-end-line="346" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20py-16%22%7D" className="container mx-auto px-4 py-16">
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:338:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="338" data-component-end-line="345" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-8%22%7D" className="space-y-8">
            {services.map((service) =>
            <div data-component-id="src/app/services/components/ServicesInteractive.tsx:340:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="340" data-component-end-line="343" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-lg%20shadow-brand-md%20p-8%20animate-pulse%22%7D" key={service.id} className="bg-card rounded-lg shadow-brand-md p-8 animate-pulse">
                <div data-component-id="src/app/services/components/ServicesInteractive.tsx:341:16" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="341" data-component-end-line="341" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-muted%20rounded%20w-1%2F3%20mb-4%22%7D" className="h-8 bg-muted rounded w-1/3 mb-4" />
                <div data-component-id="src/app/services/components/ServicesInteractive.tsx:342:16" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="342" data-component-end-line="342" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-4%20bg-muted%20rounded%20w-2%2F3%22%7D" className="h-4 bg-muted rounded w-2/3" />
              </div>
            )}
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/services/components/ServicesInteractive.tsx:352:4" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="352" data-component-end-line="403" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <ServiceHero data-component-id="src/app/services/components/ServicesInteractive.tsx:353:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="353" data-component-end-line="353" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceHero" data-component-content="%7B%22elementName%22%3A%22ServiceHero%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...heroData} />
      
      <section data-component-id="src/app/services/components/ServicesInteractive.tsx:355:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="355" data-component-end-line="377" data-component-file="ServicesInteractive.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-16%20lg%3Apy-24%20bg-background%22%7D" className="py-16 lg:py-24 bg-background">
        <div data-component-id="src/app/services/components/ServicesInteractive.tsx:356:8" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="356" data-component-end-line="376" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%22%7D" className="container mx-auto px-4">
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:357:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="357" data-component-end-line="364" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" className="text-center mb-12">
            <h2 data-component-id="src/app/services/components/ServicesInteractive.tsx:358:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="358" data-component-end-line="360" data-component-file="ServicesInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20lg%3Atext-4xl%20font-headline%20font-bold%20text-foreground%20mb-4%22%2C%22textContent%22%3A%22Our%20Service%20Portfolio%22%7D" className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
              Our Service Portfolio
            </h2>
            <p data-component-id="src/app/services/components/ServicesInteractive.tsx:361:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="361" data-component-end-line="363" data-component-file="ServicesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20font-body%20text-muted-foreground%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22Comprehensive%20solutions%20tailored%20to%20your%20unique%20business%20needs%20and%20growth%20objectives%22%7D" className="text-lg font-body text-muted-foreground max-w-2xl mx-auto">
              Comprehensive solutions tailored to your unique business needs and growth objectives
            </p>
          </div>
          
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:366:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="366" data-component-end-line="375" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-8%20max-w-7xl%20mx-auto%22%7D" className="space-y-8 max-w-7xl mx-auto">
            {services.map((service) =>
            <ServiceCard data-component-id="src/app/services/components/ServicesInteractive.tsx:368:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="368" data-component-end-line="372" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceCard" data-component-content="%7B%22elementName%22%3A%22ServiceCard%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
              key={service.id}
              {...service}
              isExpanded={expandedServices[service.id] || false}
              onToggle={() => handleToggleService(service.id)} />

            )}
          </div>
        </div>
      </section>
      
      <ServiceComparison data-component-id="src/app/services/components/ServicesInteractive.tsx:379:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="379" data-component-end-line="379" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceComparison" data-component-content="%7B%22elementName%22%3A%22ServiceComparison%22%7D" features={comparisonFeatures} />
      
      <section data-component-id="src/app/services/components/ServicesInteractive.tsx:381:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="381" data-component-end-line="398" data-component-file="ServicesInteractive.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-16%20lg%3Apy-24%20bg-background%22%7D" className="py-16 lg:py-24 bg-background">
        <div data-component-id="src/app/services/components/ServicesInteractive.tsx:382:8" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="382" data-component-end-line="397" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%22%7D" className="container mx-auto px-4">
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:383:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="383" data-component-end-line="390" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" className="text-center mb-12">
            <h2 data-component-id="src/app/services/components/ServicesInteractive.tsx:384:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="384" data-component-end-line="386" data-component-file="ServicesInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20lg%3Atext-4xl%20font-headline%20font-bold%20text-foreground%20mb-4%22%2C%22textContent%22%3A%22Client%20Success%20Stories%22%7D" className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
              Client Success Stories
            </h2>
            <p data-component-id="src/app/services/components/ServicesInteractive.tsx:387:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="387" data-component-end-line="389" data-component-file="ServicesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20font-body%20text-muted-foreground%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22Real%20results%20from%20businesses%20that%20trusted%20us%20with%20their%20digital%20transformation%22%7D" className="text-lg font-body text-muted-foreground max-w-2xl mx-auto">
              Real results from businesses that trusted us with their digital transformation
            </p>
          </div>
          
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:392:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="392" data-component-end-line="396" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-8%20max-w-7xl%20mx-auto%22%7D" className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {testimonials.map((testimonial, index) =>
            <TestimonialCard data-component-id="src/app/services/components/ServicesInteractive.tsx:394:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="394" data-component-end-line="394" data-component-file="ServicesInteractive.tsx" data-component-name="TestimonialCard" data-component-content="%7B%22elementName%22%3A%22TestimonialCard%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" key={index} {...testimonial} />
            )}
          </div>
        </div>
      </section>
      
      <IntegrationPartners data-component-id="src/app/services/components/ServicesInteractive.tsx:400:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="400" data-component-end-line="400" data-component-file="ServicesInteractive.tsx" data-component-name="IntegrationPartners" data-component-content="%7B%22elementName%22%3A%22IntegrationPartners%22%7D" partners={partners} />
      
      <CTASection data-component-id="src/app/services/components/ServicesInteractive.tsx:402:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="402" data-component-end-line="402" data-component-file="ServicesInteractive.tsx" data-component-name="CTASection" data-component-content="%7B%22elementName%22%3A%22CTASection%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...ctaData} />
    </div>);

};

export default ServicesInteractive;